/**
 * Moves all items matching the predicate to the front of the returned array.
 *
 * Element order remains otherwise the same.
 *
 * This is similar to C++'s std::partition(), but it returns a new array rather
 * than a partition point.
 */
export function prioritizeWith<T>(arr: T[], predicate: (item: T) => boolean): T[] {
  const [trueVals, falseVals] = arr.reduce(
    ([t, f], item) => (predicate(item) ? [[...t, item], f] : [t, [...f, item]]),
    [[] as T[], [] as T[]]
  )
  return [...trueVals, ...falseVals]
}

/**
 * Splits the enumerable into groups based on makeKey, mirroring Elixir's Enum.group_by/3.
 *
 * The result is an object where each key is given by makeKey and each value
 * is a list of elements that produced that key, mapped through makeVal.
 *
 * The order of elements within each list is preserved from the array.
 */
export function groupBy<T, K extends string>(
  arr: Array<T>,
  makeKey: (item: T) => K,
  makeVal: (item: T) => T = identity
): Record<K, T[]> {
  const result = {} as Record<K, T[]>

  for (const item of arr) {
    const key = makeKey(item)

    if (!result[key]) {
      result[key] = []
    }

    result[key].push(makeVal(item))
  }

  return result
}

function identity<T>(item: T): T {
  return item
}

export function maxValue<T>(arr: Array<T>, mapper: (a: T) => number): number | undefined {
  if (arr.length === 0) {
    return undefined
  }

  return arr.reduce((max, item) => Math.max(max, mapper(item)), -Infinity)
}

/**
 * Sorts an array by the values returned by mapper.
 */
export function sortBy<T>(arr: Array<T>, mapper: (a: T) => any) {
  return arr.toSorted((a, b) => mapper(a) - mapper(b))
}

export function isEqual(a: any, b: any): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}
